/**
 * Employees list — workforce master directory.
 *
 * Composition:
 *   - PageHeader + "Add employee" action
 *   - KPI strip (Active count / MTD payroll / Attendance % / Pending leaves)
 *   - Filter chips (Department, Branch, Employment type, Active/Terminated)
 *   - DataTable with photo+code, name, dept, job, branch, hired (relative + override badge), salary
 *   - Row click navigates to /workforce/employees/$employeeId
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banknote, Filter, Plus, Users } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  EmployeeForm,
  type EmployeeFormValues,
} from "../../../components/forms/EmployeeForm";
import {
  EmployeePaymentForm,
  type EmployeePaymentFormValues,
} from "../../../components/forms/EmployeePaymentForm";
import { EmployeeCard } from "../../../components/workforce/EmployeeCard";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  RequirePermission,
  useHasPermission,
} from "../../../lib/permissions";
import {
  DEPARTMENTS,
  EMPLOYMENT_TYPE_LABELS,
  WORKFORCE_BRANCHES,
  mockEmployees,
  mockEmployeePayments,
  mockLeaves,
  mockAttendance,
  type MockAttendance,
  type MockDepartment,
  type MockEmployee,
  type MockEmployeePayment,
  type MockEmploymentType,
  type MockLeave,
  type MockPaymentStatus,
} from "../../../lib/workforceMock";

export const Route = createFileRoute("/_app/workforce/employees")({
  component: EmployeesPage,
});

type ActiveFilter = "active" | "terminated" | "all";

function relativeJoin(ms: number): string {
  const diff = Date.now() - ms;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${(days / 365).toFixed(1)}yr ago`;
}

function EmployeesPage() {
  const isMock = useMockData();
  void isMock;
  const navigate = useNavigate();
  const canSeeFinance = useHasPermission("finance.read");

  const [rows, setRows] = usePersistentMockState<MockEmployee[]>("employees", mockEmployees);
  const [allPayments, setPayments] = usePersistentMockState<MockEmployeePayment[]>(
    "employeePayments",
    mockEmployeePayments,
  );
  const [allAttendance] = usePersistentMockState<MockAttendance[]>(
    "attendance",
    mockAttendance,
  );
  const [allLeaves] = usePersistentMockState<MockLeave[]>("leaves", mockLeaves);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockEmployee | null>(null);
  const [payTarget, setPayTarget] = useState<MockEmployee | null>(null);
  const canPay = useHasPermission("payroll.write");

  const [departmentFilter, setDepartmentFilter] = useState<MockDepartment | "all">("all");
  const [branchFilter, setBranchFilter] = useState<string | "all">("all");
  const [typeFilter, setTypeFilter] = useState<MockEmploymentType | "all">("all");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (departmentFilter !== "all" && r.department !== departmentFilter) return false;
      if (branchFilter !== "all" && r.branchId !== branchFilter) return false;
      if (typeFilter !== "all" && r.employmentType !== typeFilter) return false;
      if (activeFilter === "active" && !r.isActive) return false;
      if (activeFilter === "terminated" && r.isActive) return false;
      return true;
    });
  }, [rows, departmentFilter, branchFilter, typeFilter, activeFilter]);

  // KPIs
  const activeCount = useMemo(() => rows.filter((r) => r.isActive).length, [rows]);
  const mtdPayroll = useMemo(
    () =>
      allPayments
        .filter((p) => p.status !== "void")
        .reduce((s, p) => {
          const periodMs = p.periodTo;
          const d = new Date(periodMs);
          const now = new Date();
          if (
            d.getUTCFullYear() === now.getUTCFullYear() &&
            d.getUTCMonth() === now.getUTCMonth()
          ) {
            return s + p.netInBaseCents;
          }
          return s;
        }, 0n),
    [allPayments],
  );
  const attendancePct = useMemo(() => {
    const last30 = allAttendance.filter(
      (a) => a.date > Date.now() - 30 * 24 * 60 * 60 * 1000,
    );
    let presentScore = 0;
    let scheduled = 0;
    for (const a of last30) {
      if (a.status === "holiday") continue;
      scheduled += 1;
      if (a.status === "present") presentScore += 1;
      else if (a.status === "half_day") presentScore += 0.5;
    }
    return scheduled === 0 ? 0 : Math.round((presentScore / scheduled) * 100);
  }, [allAttendance]);
  const pendingLeaves = useMemo(
    () => allLeaves.filter((l) => l.status === "pending").length,
    [allLeaves],
  );

  function handlePay(values: EmployeePaymentFormValues) {
    if (!payTarget) return;
    const status: MockPaymentStatus = values.markPaid ? "paid" : "draft";
    const now = Date.now();
    const newPayment: MockEmployeePayment = {
      _id: `pay_local_${now}_${Math.random().toString(36).slice(2, 7)}`,
      employeeId: payTarget._id,
      paymentType: values.paymentType,
      periodFrom: new Date(values.periodFrom).getTime(),
      periodTo: new Date(values.periodTo).getTime(),
      baseSalaryCents: values.baseSalaryCents,
      overtimeCents: values.overtimeCents,
      bonusCents: values.bonusCents,
      deductionsCents: values.deductionsCents,
      netCents: values.netCents,
      currency: values.currency,
      fxRateToBaseE8: 100_000_000n,
      netInBaseCents: values.netCents,
      paidAt: status === "paid" ? new Date(values.paidAt).getTime() : null,
      paymentMethod: values.paymentMethod,
      referenceNumber: values.referenceNumber || null,
      status,
      notes: values.notes || null,
    };
    setPayments((prev) => [newPayment, ...prev]);
    const name = payTarget.fullName;
    setPayTarget(null);
    toast.success(
      status === "paid"
        ? `Payment marked as paid for ${name}.`
        : `Payment recorded for ${name}.`,
    );
  }

  function handleCreate(values: EmployeeFormValues) {
    const now = Date.now();
    const branch = WORKFORCE_BRANCHES.find((b) => b.id === values.branchId);
    const newRow: MockEmployee = {
      _id: `emp_local_${now}`,
      code: values.code,
      userId: null,
      fullName: values.fullName,
      displayName: values.displayName || values.fullName.split(" ")[0] || values.fullName,
      photoUrl: values.photoUrl,
      employmentType: values.employmentType,
      department: values.department,
      branchId: values.branchId,
      branchLabel: branch?.displayName ?? null,
      jobTitle: values.jobTitle,
      hiredAt: new Date(values.hiredAt).getTime(),
      hiredAtIsOverride:
        new Date(values.hiredAt).getTime() <
        Date.UTC(2024, 0, 1),
      terminatedAt: null,
      baseSalaryCents: values.baseSalaryCents,
      salaryCurrency: values.salaryCurrency,
      payCadence: values.payCadence,
      nationalId: values.nationalId || null,
      phone: values.phone,
      email: values.email || null,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2 || null,
      addressCity: values.addressCity,
      addressDistrict: values.addressDistrict,
      addressPostalCode: values.addressPostalCode || null,
      emergencyContact: values.emergencyContact,
      bankAccount: values.bankAccount,
      notes: values.notes || null,
      isActive: true,
      createdAt: now,
      updatedAt: now,
    };
    setRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`Added ${newRow.fullName}`, {
      description: `${newRow.code} · ${newRow.jobTitle}`,
    });
  }

  const columns = useMemo<ColumnDef<MockEmployee>[]>(
    () => [
      {
        id: "card",
        header: "Employee",
        cell: ({ row }) => <EmployeeCard employee={row.original} size="sm" />,
      },
      {
        accessorKey: "department",
        header: "Department",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm">
            {row.original.department}
          </Badge>
        ),
      },
      {
        accessorKey: "jobTitle",
        header: "Job title",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">{row.original.jobTitle}</span>
        ),
      },
      {
        accessorKey: "branchLabel",
        header: "Branch",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.branchLabel ?? "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "hiredAt",
        header: "Hired",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-(--text-tertiary)">
            <span className="tabular-nums">{relativeJoin(row.original.hiredAt)}</span>
            {row.original.hiredAtIsOverride ? (
              <Badge variant="info" size="sm" title="Hired date manually overridden">
                override
              </Badge>
            ) : null}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        id: "salary",
        header: "Salary",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) => {
          if (!canSeeFinance) {
            return <span className="text-(--text-tertiary)">—</span>;
          }
          return (
            <span className="font-mono tabular-nums text-(--text-primary)">
              {formatMoney(row.original.baseSalaryCents, row.original.salaryCurrency, {
                display: row.original.salaryCurrency === "BDT" ? "symbol" : "code",
                locale: "en-BD",
              })}
              <span className="text-(--text-tertiary) text-[11px] ml-1">
                /{row.original.payCadence === "monthly" ? "mo" : "2w"}
              </span>
            </span>
          );
        },
      },
      {
        accessorKey: "isActive",
        header: "Status",
        cell: ({ row }) =>
          row.original.terminatedAt !== null ? (
            <StatusPill variant="neutral" size="sm">Terminated</StatusPill>
          ) : row.original.isActive ? (
            <StatusPill variant="success" size="sm">Active</StatusPill>
          ) : (
            <StatusPill variant="warning" size="sm">Inactive</StatusPill>
          ),
      },
      {
        id: "quickActions",
        header: () => <span className="sr-only">Quick actions</span>,
        meta: { className: "w-[1%] text-right" },
        cell: ({ row }) => {
          const emp = row.original;
          if (!canPay) return null;
          if (!emp.isActive || emp.terminatedAt !== null) {
            return <span className="text-(--text-tertiary) text-[11px]">—</span>;
          }
          return (
            <Tooltip
              content={`Record a payment for ${emp.displayName}`}
              side="left"
            >
              <button
                type="button"
                aria-label={`Pay ${emp.fullName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setPayTarget(emp);
                }}
                className={cn(
                  "inline-flex items-center gap-1 h-6 px-2 rounded-full",
                  "border border-(--success-soft) bg-(--success-soft) text-(--success)",
                  "text-[11px] font-medium leading-none",
                  "transition-[background-color,border-color,transform] duration-(--duration-instant)",
                  "hover:bg-(--success) hover:text-(--success-fg) hover:border-(--success)",
                  "active:scale-[0.97]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--success) focus-visible:ring-offset-1 focus-visible:ring-offset-(--bg-canvas)",
                )}
              >
                <Banknote className="h-3 w-3" aria-hidden />
                Pay
              </button>
            </Tooltip>
          );
        },
      },
    ],
    [canSeeFinance, canPay],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    // Salary column gated on finance.read — non-finance roles get a redacted
    // export with the salary cells set to empty.
    const baseColumns: ExportColumn<MockEmployee>[] = [
      { key: "code", label: "Code" },
      { key: "fullName", label: "Full name" },
      { key: "jobTitle", label: "Job title" },
      { key: "department", label: "Department" },
      { key: "employmentType", label: "Employment type" },
      {
        key: "branchLabel",
        label: "Branch",
        format: (v) => (v == null ? "" : String(v)),
      },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email", format: (v) => (v == null ? "" : String(v)) },
      { key: "hiredAt", label: "Hired", format: (v) => formatDateForCsv(Number(v)) },
      {
        key: "terminatedAt",
        label: "Terminated",
        format: (v) => formatDateForCsv(v as number | null),
      },
      { key: "isActive", label: "Active" },
    ];
    const salaryColumns: ExportColumn<MockEmployee>[] = canSeeFinance
      ? [
          { key: "baseSalaryCents", label: "Base salary" },
          { key: "salaryCurrency", label: "Currency" },
          { key: "payCadence", label: "Pay cadence" },
        ]
      : [];
    const columns = [...baseColumns, ...salaryColumns];
    downloadCsv(
      exportFilename("employees", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Employees"
        description="Workforce master directory — factory, office, sales, warehouse. Profiles include attendance, payroll, and linked invoices."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <RequirePermission permission="employees.write">
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" /> Add employee
              </Button>
            </RequirePermission>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Workforce KPIs"
      >
        <KpiCard
          caption="Active employees"
          value={activeCount}
          hint={`Of ${rows.length} total`}
        />
        <KpiCard
          caption="MTD payroll"
          value={
            canSeeFinance
              ? formatMoneyCompact(mtdPayroll, "BDT", { locale: "en-BD" })
              : "Restricted"
          }
          hint={canSeeFinance ? "Net paid + drafts this month" : "finance.read needed"}
        />
        <KpiCard
          caption="Attendance"
          value={`${attendancePct}%`}
          hint="Last 30 days"
        />
        <KpiCard
          caption="Pending leaves"
          value={pendingLeaves}
          hint="Awaiting approval"
        />
      </section>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        <FilterChip
          active={departmentFilter === "all"}
          onClick={() => setDepartmentFilter("all")}
        >
          All depts
        </FilterChip>
        {DEPARTMENTS.map((d) => (
          <FilterChip
            key={d}
            active={departmentFilter === d}
            onClick={() => setDepartmentFilter(d)}
          >
            {d}
          </FilterChip>
        ))}
        <span className="mx-1 text-(--text-tertiary) text-[11px]">·</span>
        <FilterChip
          active={branchFilter === "all"}
          onClick={() => setBranchFilter("all")}
        >
          All branches
        </FilterChip>
        {WORKFORCE_BRANCHES.map((b) => (
          <FilterChip
            key={b.id}
            active={branchFilter === b.id}
            onClick={() => setBranchFilter(b.id)}
          >
            {b.displayName}
          </FilterChip>
        ))}
        <span className="mx-1 text-(--text-tertiary) text-[11px]">·</span>
        <FilterChip
          active={typeFilter === "all"}
          onClick={() => setTypeFilter("all")}
        >
          Any type
        </FilterChip>
        {(Object.keys(EMPLOYMENT_TYPE_LABELS) as MockEmploymentType[]).map(
          (t) => (
            <FilterChip
              key={t}
              active={typeFilter === t}
              onClick={() => setTypeFilter(t)}
            >
              {EMPLOYMENT_TYPE_LABELS[t]}
            </FilterChip>
          ),
        )}
        <span className="mx-1 text-(--text-tertiary) text-[11px]">·</span>
        <FilterChip
          active={activeFilter === "active"}
          onClick={() => setActiveFilter("active")}
        >
          Active
        </FilterChip>
        <FilterChip
          active={activeFilter === "terminated"}
          onClick={() => setActiveFilter("terminated")}
        >
          Terminated
        </FilterChip>
        <FilterChip
          active={activeFilter === "all"}
          onClick={() => setActiveFilter("all")}
        >
          All
        </FilterChip>
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No employees match"
        emptyDescription="Adjust filters or add a new employee to populate the directory."
        emptyIcon={<Users className="h-5 w-5" aria-hidden />}
      />

      {/* Add */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Add employee</SheetTitle>
            <SheetDescription>
              Capture identity, employment, compensation, contact, and bank.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <EmployeeForm
              branches={WORKFORCE_BRANCHES}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/*
       * Per-employee Pay sheet — opened from the row chip. Pre-fills with the
       * employee's base salary and the most recent calendar month so the
       * common case is one-click. The user can still adjust period / OT /
       * bonus / deductions before submitting.
       */}
      <Sheet
        open={payTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPayTarget(null);
        }}
      >
        <SheetContent side="right" size="md">
          {payTarget ? (
            <>
              <SheetHeader>
                <SheetTitle>Pay {payTarget.fullName}</SheetTitle>
                <SheetDescription>
                  {payTarget.code} · {payTarget.jobTitle} · pre-filled with the
                  most recent pay period and base salary.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <EmployeePaymentForm
                  employeeId={payTarget._id}
                  initialValues={{
                    baseSalaryCents: payTarget.baseSalaryCents,
                    currency: payTarget.salaryCurrency,
                    netCents: payTarget.baseSalaryCents,
                  }}
                  onSubmit={handlePay}
                  onCancel={() => setPayTarget(null)}
                />
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Employee detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow ? (
            <EmployeeDetailPanel
              row={detailRow}
              attendance={allAttendance}
              payments={allPayments}
              leaves={allLeaves}
              canSeeFinance={canSeeFinance}
              onClose={() => setDetailRow(null)}
              onViewFull={() => {
                const id = detailRow._id;
                setDetailRow(null);
                void navigate({
                  to: "/workforce/employees/$employeeId",
                  params: { employeeId: id },
                });
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Employee detail panel — tabs: Overview, Attendance, Payroll, Leaves
 * ───────────────────────────────────────────────────────────── */

function EmployeeDetailPanel({
  row,
  attendance,
  payments,
  leaves,
  canSeeFinance,
  onClose,
  onViewFull,
}: {
  row: MockEmployee;
  attendance: MockAttendance[];
  payments: MockEmployeePayment[];
  leaves: MockLeave[];
  canSeeFinance: boolean;
  onClose: () => void;
  onViewFull: () => void;
}) {
  // Employee-scoped data
  const empAttendance = useMemo(
    () =>
      attendance
        .filter((a) => a.employeeId === row._id)
        .sort((a, b) => b.date - a.date),
    [attendance, row._id],
  );
  const empPayments = useMemo(
    () =>
      payments
        .filter((p) => p.employeeId === row._id)
        .sort((a, b) => b.periodTo - a.periodTo),
    [payments, row._id],
  );
  const empLeaves = useMemo(
    () =>
      leaves
        .filter((l) => l.employeeId === row._id)
        .sort((a, b) => b.fromDate - a.fromDate),
    [leaves, row._id],
  );

  const [showAllAttendance, setShowAllAttendance] = useState(false);
  const [showAllPayroll, setShowAllPayroll] = useState(false);
  const [showAllLeaves, setShowAllLeaves] = useState(false);

  const visibleAttendance = showAllAttendance ? empAttendance : empAttendance.slice(0, 10);
  const visiblePayroll = showAllPayroll ? empPayments : empPayments.slice(0, 10);
  const visibleLeaves = showAllLeaves ? empLeaves : empLeaves.slice(0, 10);

  function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString("en-BD", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  }

  const statusColor: Record<string, string> = {
    present: "text-(--success)",
    absent: "text-(--danger)",
    half_day: "text-[var(--warning)]",
    leave: "text-(--text-tertiary)",
    holiday: "text-(--accent)",
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span>{row.fullName}</span>
          {row.isActive ? (
            <StatusPill variant="success" size="sm">Active</StatusPill>
          ) : row.terminatedAt ? (
            <StatusPill variant="neutral" size="sm">Terminated</StatusPill>
          ) : (
            <StatusPill variant="warning" size="sm">Inactive</StatusPill>
          )}
        </SheetTitle>
        <SheetDescription>
          <span className="font-mono text-[12px]">{row.code}</span>
          {` · ${row.jobTitle}`}
          {row.department ? ` · ${row.department}` : null}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="attendance">Attendance</TabsTrigger>
            <TabsTrigger value="payroll">Payroll</TabsTrigger>
            <TabsTrigger value="leaves">Leaves</TabsTrigger>
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
              <section className="grid grid-cols-2 gap-3">
                <DetailField label="Full name" value={row.fullName} />
                <DetailField label="Job title" value={row.jobTitle} />
                <DetailField label="Department" value={row.department} />
                <DetailField label="Branch" value={row.branchLabel ?? "—"} />
                <DetailField label="Employment type" value={row.employmentType.replace("_", " ")} />
                <DetailField label="Hired" value={formatDate(row.hiredAt)} />
                {canSeeFinance ? (
                  <DetailField
                    label="Salary"
                    value={formatMoney(row.baseSalaryCents, row.salaryCurrency, {
                      locale: "en-BD",
                      display: "code",
                    })}
                  />
                ) : (
                  <DetailField label="Salary" value="Restricted" />
                )}
                <DetailField label="Pay cadence" value={row.payCadence} />
              </section>

              <section className="grid grid-cols-2 gap-3">
                <DetailField label="Phone" value={row.phone} />
                <DetailField label="Email" value={row.email ?? "—"} />
                <DetailField label="Address" value={`${row.addressLine1}, ${row.addressCity}`} />
                <DetailField label="District" value={row.addressDistrict} />
              </section>

              {row.emergencyContact ? (
                <section className="rounded-[var(--radius-md)] border border-(--border-subtle) p-3">
                  <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
                    Emergency contact
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <DetailField label="Name" value={row.emergencyContact.name} />
                    <DetailField label="Relation" value={row.emergencyContact.relation} />
                    <DetailField label="Phone" value={row.emergencyContact.phone} />
                  </div>
                </section>
              ) : null}
            </div>
          </TabsContent>

          {/* ── Attendance ── */}
          <TabsContent value="attendance" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Recent attendance ({empAttendance.length})
              </div>
              {empAttendance.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No attendance records.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visibleAttendance.map((a) => (
                      <li
                        key={a._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[12px] tabular-nums text-(--text-secondary)">
                            {formatDate(a.date)}
                          </span>
                          <span className={`text-[12px] font-medium capitalize ${statusColor[a.status] ?? ""}`}>
                            {a.status.replace("_", " ")}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-(--text-tertiary)">
                          {a.workedMinutes > 0 ? (
                            <span className="tabular-nums">
                              {Math.floor(a.workedMinutes / 60)}h {a.workedMinutes % 60}m
                            </span>
                          ) : null}
                          {a.overtimeMinutes > 0 ? (
                            <span className="tabular-nums text-[var(--warning)]">
                              +{a.overtimeMinutes}m OT
                            </span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {empAttendance.length > 10 && !showAllAttendance ? (
                    <button
                      type="button"
                      onClick={() => setShowAllAttendance(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {empAttendance.length} records →
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </TabsContent>

          {/* ── Payroll ── */}
          <TabsContent value="payroll" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Payment history ({empPayments.length})
              </div>
              {empPayments.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No payroll records.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visiblePayroll.map((p) => (
                      <li
                        key={p._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="text-[12px] tabular-nums text-(--text-secondary)">
                            {formatDate(p.periodFrom)} - {formatDate(p.periodTo)}
                          </span>
                          <Badge
                            variant={p.status === "paid" ? "success" : p.status === "void" ? "neutral" : "info"}
                            size="sm"
                          >
                            {p.status}
                          </Badge>
                          <span className="text-[11px] text-(--text-tertiary) capitalize">
                            {p.paymentType}
                          </span>
                        </div>
                        {canSeeFinance ? (
                          <span className="font-mono tabular-nums text-[13px]">
                            {formatMoney(p.netCents, p.currency, {
                              locale: "en-BD",
                              display: "code",
                            })}
                          </span>
                        ) : (
                          <span className="text-(--text-tertiary) text-[12px]">—</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {empPayments.length > 10 && !showAllPayroll ? (
                    <button
                      type="button"
                      onClick={() => setShowAllPayroll(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {empPayments.length} payments →
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </TabsContent>

          {/* ── Leaves ── */}
          <TabsContent value="leaves" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Leave history ({empLeaves.length})
              </div>
              {empLeaves.length === 0 ? (
                <div className="text-[12px] text-(--text-tertiary)">
                  No leave records.
                </div>
              ) : (
                <>
                  <ul className="flex flex-col gap-1.5">
                    {visibleLeaves.map((l) => (
                      <li
                        key={l._id}
                        className="flex items-center justify-between rounded-[var(--radius-md)] border border-(--border-subtle) px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className="text-[12px] tabular-nums text-(--text-secondary)">
                            {formatDate(l.fromDate)} - {formatDate(l.toDate)}
                          </span>
                          <Badge
                            variant={
                              l.status === "approved"
                                ? "success"
                                : l.status === "rejected"
                                  ? "danger"
                                  : "warning"
                            }
                            size="sm"
                          >
                            {l.status}
                          </Badge>
                          <span className="text-[11px] text-(--text-tertiary) capitalize">
                            {l.leaveType}
                          </span>
                        </div>
                        <span className="text-[12px] text-(--text-secondary) tabular-nums">
                          {l.days} day{l.days !== 1 ? "s" : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {empLeaves.length > 10 && !showAllLeaves ? (
                    <button
                      type="button"
                      onClick={() => setShowAllLeaves(true)}
                      className="text-[12px] text-(--accent) hover:underline self-start"
                    >
                      Show all {empLeaves.length} leaves →
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button onClick={onViewFull}>
          View full page
        </Button>
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
        active
          ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
          : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}
